import { IAppState } from '../IAppState';
import * as R from 'ramda';
import { ApiLoading } from './apiLoading';
import { ApiError } from './apiError';
import { isNilOrEmpty, not, assoc } from '../../../language/module';
import { ACTION_TYPE } from '../actionType';


export const keyFor = (actionType: string, requestId: string): string => actionType + requestId;


/**
 * Given an app state, guestID and actionType, inserts a loading model into app state.
 * Immutable, returns a new app state and doesn't mutate the previous app state. Also removes any matching errors,
 * because you can't already have an error if you're just now entering loading state. If you use this, you want to use
 * getLoading/removeLoading to get/remove loadings.
 */
export const addLoading = (
  state: IAppState,
  requestId: string,
  actionType: string,
  extras?: any,
): IAppState => ({
  ...state,
  loading: state.loading.add(keyFor(actionType, requestId), { requestId, actionType, extras }),
  error:   state.error.remove(keyFor(actionType, requestId)),
});


/**
 * Removes the loading model with the given GuestID and ActionType from the given App State. Only works on loadings that
 * were added with addLoading.
 */
export const removeLoading = (state: IAppState, requestId: string, actionType: string): IAppState => assoc(
  'loading',
  state.loading.remove(keyFor(actionType, requestId)),
  state,
);


/**
 * Returns a loading state for the given GuestID + ActionType combo if it exists. This will only work if you used
 * addLoading to put the loading action into app state.
 */
export const getLoading = (
  state: IAppState,
  requestId: string,
  actionType: string,
): ApiLoading | undefined => state.loading.get(keyFor(actionType, requestId));


/**
 * Whether a newer request for the same entity went out after `requestId`, making this response out of date. An
 * out-of-date response must leave the entity's loading entry, error and data to the newer request.
 *
 * While a request is in flight, its loading entry names it, provided the reducer passed `{ requestId }` as addLoading's
 * extras. An entry that names no request never counts as newer. With no entry, any newer request has already landed,
 * and `latestRequestId` says whether there was one.
 */
export const isSupersededRequest = (
  state: IAppState,
  entityId: string,
  actionType: string,
  requestId: string,
  latestRequestId: string | undefined,
): boolean => {
  const loading = getLoading(state, entityId, actionType);
  return loading
    ? loading.extras?.requestId !== undefined && loading.extras.requestId !== requestId
    : !!latestRequestId && latestRequestId !== requestId;
};


/**
 * Makes a new app state with the given error information. Also removes any matching loading errors, because you can't
 * still be loading if you know you have an error. If you use this to add the error, you want to use getError to pull
 * the error out of app state.
 */
export const addError = (
  state: IAppState,
  requestId: string,
  actionType: string,
  errorMsg: string,
  extras?: any,
): IAppState => ({
  ...state,
  loading: state.loading.remove(keyFor(actionType, requestId)),
  error:   state.error.add(keyFor(actionType, requestId), { requestId, actionType, errorMsg: errorMsg, extras }),
});


/**
 * Makes a new app state with the given error information removed from the errors property.
 */
export const removeError = (state: IAppState, requestId: string, actionType: string): IAppState => assoc(
  'error',
  state.error.remove(keyFor(actionType, requestId)),
  state,
);


/**
 * Returns the given error information found by action type and guest id if it exists. Only works for errors added with
 * addError
 */
export const getError = (
  state: IAppState,
  requestId: string,
  actionType: string,
): ApiError | undefined => state.error.get(keyFor(actionType, requestId));


/**
 * Removes loading or error states for the given guestID/action combo, returning a new IAppState with the results. Only
 * works for loading/errors added with addLoading/Error.
 */
export const removeLoadingAndError = (
  state: IAppState,
  requestId: string,
  actionType: string,
) => removeLoading(removeError(state, requestId, actionType), requestId, actionType);


/**
 * Returns the given error actions information found by action type and guest id if it exists. Only returns errors added
 * with addError
 */
export const getErrors = (
  state: IAppState,
  requestIds: string[],
  actionType: string,
): ApiError[] => requestIds.map(id => state.error.get(keyFor(actionType, id))).filter(not(isNilOrEmpty));


/**
 * Returns all of the errors in app state that have the given action type.
 */
export const errorsByActionType = (
  state: IAppState,
  actionType: ACTION_TYPE,
) => state.error.filter((key, error) => error.actionType === actionType).getValues();


/**
 * Null-friendly function that returns a loading state for the given action/app state/requestId if it exists, otherwise
 * returns undefined.
 */
export const getLoadingShortCircuit = R.curry(
  (
    action: string,
    state: IAppState,
    requestId: string | null | undefined,
  ): ApiLoading | undefined => !requestId ? undefined : getLoading(state, requestId, action),
);


/**
 * Null-friendly function that returns an error state for the given action/app state/requestId if it exists, otherwise
 * returns undefined.
 */
export const getErrorShortCircuit = R.curry(
  (
    action: string,
    state: IAppState,
    requestId: string | null | undefined,
  ): ApiError | undefined => !requestId ? undefined : getError(state, requestId, action),
);


